import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ExternalLink, Star, Eye, EyeOff, ImagePlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { apiClient, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  publication_name: "",
  headline: "",
  excerpt: "",
  article_url: "",
  published_date: "",
  is_published: false,
};

export const NewsManager = () => {
  const [articles, setArticles] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchArticles = async () => {
    try {
      const { data } = await apiClient.get("/admin/news");
      setArticles(data);
    } catch {
      toast.error("Could not load news articles");
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const openAddForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setThumbnailFile(null);
    setFormOpen(true);
  };

  const openEditForm = (article) => {
    setEditingId(article.id);
    setForm({
      publication_name: article.publication_name,
      headline: article.headline,
      excerpt: article.excerpt,
      article_url: article.article_url,
      published_date: article.published_date,
      is_published: article.is_published,
    });
    setThumbnailFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setThumbnailFile(null);
  };

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.publication_name.trim() || !form.headline.trim() || !form.article_url.trim()) {
      toast.error("Publication, headline and article URL are required");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
    if (thumbnailFile) fd.append("thumbnail", thumbnailFile);

    try {
      if (editingId) {
        await apiClient.put(`/admin/news/${editingId}`, fd);
        toast.success("Article updated");
      } else {
        await apiClient.post("/admin/news", fd);
        toast.success("Article added");
      }
      closeForm();
      fetchArticles();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this article from the News & Media section?")) return;
    try {
      await apiClient.delete(`/admin/news/${id}`);
      toast.success("Article removed");
      fetchArticles();
    } catch {
      toast.error("Could not remove article");
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      await apiClient.patch(`/admin/news/${article.id}/publish`);
      toast.success(article.is_published ? "Article moved to draft" : "Article published");
      fetchArticles();
    } catch {
      toast.error("Could not update publish status");
    }
  };

  const handleToggleFeature = async (article) => {
    try {
      await apiClient.patch(`/admin/news/${article.id}/feature`);
      toast.success(article.is_featured ? "Removed from featured" : "Pinned as featured article");
      fetchArticles();
    } catch {
      toast.error("Could not update featured status");
    }
  };

  return (
    <div data-testid="admin-news-manager">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#A1B0A6]">
          Articles shown on the public Resources page, News and Media tab. Drafts stay hidden from
          visitors until published. One article can be pinned as featured.
        </p>
        <button
          onClick={openAddForm}
          data-testid="admin-news-add-btn"
          className="flex items-center gap-1.5 text-sm font-medium bg-gold hover:bg-gold-hover text-[#0A140F] rounded-full px-4 py-2 transition-colors shrink-0"
        >
          <Plus size={14} /> Add Article
        </button>
      </div>

      {formOpen && (
        <div
          data-testid="admin-news-form"
          className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 mb-6 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="font-display font-semibold text-[#F4F7F5]">
              {editingId ? "Edit Article" : "New Article"}
            </span>
            <button onClick={closeForm} data-testid="admin-news-cancel-btn" className="text-[#A1B0A6] hover:text-gold">
              <X size={16} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              value={form.publication_name}
              onChange={(e) => setField("publication_name", e.target.value)}
              placeholder="Publication name"
              data-testid="admin-news-input-publication"
              className="bg-deep border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
            />
            <input
              value={form.published_date}
              onChange={(e) => setField("published_date", e.target.value)}
              placeholder="Published date (e.g. July 7, 2025)"
              data-testid="admin-news-input-date"
              className="bg-deep border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
            />
          </div>
          <input
            value={form.headline}
            onChange={(e) => setField("headline", e.target.value)}
            placeholder="Article headline"
            data-testid="admin-news-input-headline"
            className="w-full bg-deep border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
          />
          <Textarea
            value={form.excerpt}
            onChange={(e) => setField("excerpt", e.target.value)}
            placeholder="One-line excerpt"
            rows={2}
            data-testid="admin-news-textarea-excerpt"
            className="bg-deep border-gold/15 text-[#F4F7F5] placeholder:text-[#6E7E74]"
          />
          <input
            value={form.article_url}
            onChange={(e) => setField("article_url", e.target.value)}
            placeholder="Article URL (https://...)"
            data-testid="admin-news-input-url"
            className="w-full bg-deep border border-gold/15 rounded-md px-3 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
          />

          <div>
            <label
              htmlFor="admin-news-thumbnail-input"
              data-testid="admin-news-thumbnail-label"
              className="flex items-center gap-2 text-sm text-[#A1B0A6] hover:text-gold cursor-pointer w-fit"
            >
              <ImagePlus size={15} />
              {thumbnailFile ? thumbnailFile.name : "Attach a thumbnail photo (optional)"}
            </label>
            <input
              id="admin-news-thumbnail-input"
              type="file"
              accept="image/*"
              onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
              data-testid="admin-news-thumbnail-input"
              className="hidden"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-[#D7E0DA] cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setField("is_published", e.target.checked)}
              data-testid="admin-news-publish-checkbox"
              className="accent-gold h-4 w-4"
            />
            Publish immediately (visible on the public Resources page)
          </label>

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-news-save-btn"
            className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] text-sm font-display font-semibold px-5 py-2 rounded-md transition-colors"
          >
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add Article"}
          </button>
        </div>
      )}

      {!articles ? (
        <p className="text-[#A1B0A6] text-sm">Loading articles...</p>
      ) : articles.length === 0 ? (
        <p className="text-[#A1B0A6] text-sm" data-testid="admin-news-empty">No articles yet.</p>
      ) : (
        <div className="space-y-3">
          {articles.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-news-card-${a.id}`}
              className="rounded-xl border border-gold/15 bg-deep-card p-4 sm:p-5 flex items-start gap-4"
            >
              {a.thumbnail_path && (
                <img
                  src={`${API}/news/thumbnail/${a.thumbnail_path}`}
                  alt={a.headline}
                  data-testid={`admin-news-thumbnail-${a.id}`}
                  className="h-16 w-16 rounded-lg object-cover shrink-0 border border-gold/15"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-gold">{a.publication_name}</span>
                  <span
                    data-testid={`admin-news-status-badge-${a.id}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      a.is_published ? "bg-emerald-500/20 text-emerald-300" : "bg-[#6E7E74]/25 text-[#A1B0A6]"
                    }`}
                  >
                    {a.is_published ? "Published" : "Draft"}
                  </span>
                  {a.is_featured && (
                    <span
                      data-testid={`admin-news-featured-badge-${a.id}`}
                      className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/20 text-gold"
                    >
                      <Star size={10} fill="currentColor" /> Featured
                    </span>
                  )}
                </div>
                <div className="font-display font-semibold text-[#F4F7F5] mt-1">{a.headline}</div>
                <p className="text-sm text-[#A1B0A6] mt-1">{a.excerpt}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-[#6E7E74]">
                  <span>{a.published_date}</span>
                  <a href={a.article_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-gold hover:text-gold-hover">
                    View link <ExternalLink size={11} />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => handleTogglePublish(a)}
                  data-testid={`admin-news-publish-btn-${a.id}`}
                  title={a.is_published ? "Move to draft" : "Publish"}
                  className="text-[#A1B0A6] hover:text-gold p-1.5"
                >
                  {a.is_published ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button
                  onClick={() => handleToggleFeature(a)}
                  data-testid={`admin-news-feature-btn-${a.id}`}
                  title={a.is_featured ? "Unpin featured" : "Pin as featured"}
                  className={a.is_featured ? "text-gold p-1.5" : "text-[#A1B0A6] hover:text-gold p-1.5"}
                >
                  <Star size={15} fill={a.is_featured ? "currentColor" : "none"} />
                </button>
                <button
                  onClick={() => openEditForm(a)}
                  data-testid={`admin-news-edit-btn-${a.id}`}
                  className="text-[#A1B0A6] hover:text-gold p-1.5"
                >
                  <Pencil size={15} />
                </button>
                <button
                  onClick={() => handleDelete(a.id)}
                  data-testid={`admin-news-delete-btn-${a.id}`}
                  className="text-[#A1B0A6] hover:text-red-400 p-1.5"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
